const ALLOWED_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'hr',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'small',
  'ul',
  'ol',
  'li',
  'a',
])

const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
}

// Allowlist ampla do conteúdo SEO dos hotsites, equivalente ao perfil CONTEUDO_SEO do
// SeoHtmlSanitizerService (ecommerce-api): o servidor já sanitiza ao gravar, e esta é a
// segunda barreira antes do SSR. TNDC-436, P2.
const WIDE_ALLOWED_TAGS = new Set([
  ...Array.from(ALLOWED_TAGS),
  's',
  'font',
  'blockquote',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'img',
  'iframe',
])

const WIDE_ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  ...ALLOWED_ATTRIBUTES,
  font: new Set(['color']),
  img: new Set(['src', 'alt', 'width', 'height']),
  iframe: new Set(['src', 'width', 'height', 'frameborder', 'allowfullscreen']),
}

const WIDE_ALLOWED_CSS_PROPERTIES = new Set([
  'color',
  'background-color',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
  'width',
  'height',
])

// Cores, medidas e palavras-chave; função só rgb/hsl. Fica de fora tudo que carrega ou
// executa algo (url(), expression(), image-set()) e o escape `\` que os disfarça.
const SAFE_CSS_VALUE = /^(?:[#\w.%\s,-]|(?:rgba?|hsla?)\(\s*[\d.%\s,]+\))+$/i
const SAFE_DIMENSION = /^\d+(?:\.\d+)?(?:px|%)?$/
const SAFE_COLOR = /^#?[\w]+$/
const SAFE_IFRAME_SRC =
  /^(?:https:)?\/\/(?:www\.youtube(?:-nocookie)?\.com\/embed\/|player\.vimeo\.com\/video\/)/i
// Sem SVG: é o único formato de imagem que carrega script.
const SAFE_DATA_IMAGE =
  /^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i

// Resposta do FAQ: a whitelist do critério 8, igual ao perfil FAQ do
// SeoHtmlSanitizerService (ecommerce-api). Sem imagem, tabela, vídeo, estilo nem classe;
// o h1 vira h2 (um único h1 na página). TNDC-437.
const FAQ_ALLOWED_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'div',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'ul',
  'ol',
  'li',
  'a',
])

type SanitizeSeoHtmlOptions = {
  allowlist?: 'default' | 'wide' | 'faq'
}

const ELEMENTS_WITH_UNSAFE_CONTENT =
  /<\s*(script|style|iframe|object|embed|noscript|template)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi

// Mídia sai com o conteúdo alternativo ("seu navegador não suporta vídeo"), como no servidor.
const FAQ_ELEMENTS_WITH_UNSAFE_CONTENT =
  /<\s*(script|style|iframe|object|embed|noscript|template|video|audio)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi

const WIDE_ELEMENTS_WITH_UNSAFE_CONTENT =
  /<\s*(script|style|object|embed|noscript|template)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi

const IFRAME_BLOCK =
  /<\s*iframe\b((?:"[^"]*"|'[^']*'|[^>"'])*)>[\s\S]*?(?:<\s*\/\s*iframe\s*>|$)/gi

const VOID_TAGS = new Set(['br', 'hr', 'img'])

const closeTags = (tags: string[]) => tags.reverse().map((tag) => `</${tag}>`)

const COMMENTS = /<!--[\s\S]*?(?:-->|$)/g
const TAG =
  /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^>"'])*?)\/?\s*>/g
const ATTRIBUTE =
  /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g

const decodeForComparison = (value: string) =>
  value
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/&#(\d+);?/g, (_, dec) => String.fromCharCode(Number(dec)))
    .split('')
    .filter((character) => {
      const code = character.charCodeAt(0)
      return code > 32 && code !== 127
    })
    .join('')
    .toLowerCase()

// Allowlist dupla, deny-by-default. Entidade nomeada (&colon; &Tab;) não é
// decodificada aqui, então o prefixo que a contiver é recusado em vez de adivinhado.
const isSafeHref = (value: string) => {
  const comparable = decodeForComparison(value)
  const pathStart = comparable.search(/[/?#]/)
  const prefix = pathStart === -1 ? comparable : comparable.slice(0, pathStart)

  if (prefix.includes('&')) return false

  const scheme = prefix.match(/^([a-z][a-z0-9+.-]*):/)

  return !scheme || ['http', 'https', 'mailto', 'tel'].includes(scheme[1])
}

const isSafeImageSrc = (value: string) =>
  SAFE_DATA_IMAGE.test(value.trim()) ||
  (!decodeForComparison(value).startsWith('data:') && isSafeHref(value))

const sanitizeStyle = (value: string) =>
  value
    .split(';')
    .map((declaration) => {
      const separator = declaration.indexOf(':')
      if (separator === -1) return null

      const property = declaration.slice(0, separator).trim().toLowerCase()
      const cssValue = declaration.slice(separator + 1).trim()

      return WIDE_ALLOWED_CSS_PROPERTIES.has(property) &&
        SAFE_CSS_VALUE.test(cssValue)
        ? `${property}: ${cssValue}`
        : null
    })
    .filter(Boolean)
    .join('; ')

// Valor aceito (eventualmente reescrito) ou null para descartar o atributo.
const wideAttributeValue = (tag: string, name: string, value: string) => {
  if (name === 'style') return sanitizeStyle(value) || null
  if (tag === 'img' && name === 'src')
    return isSafeImageSrc(value) ? value : null
  if (tag === 'iframe' && name === 'src')
    return SAFE_IFRAME_SRC.test(value.trim()) ? value : null
  if (['width', 'height', 'frameborder'].includes(name))
    return SAFE_DIMENSION.test(value.trim()) ? value.trim() : null
  if (name === 'color')
    return SAFE_COLOR.test(value.trim()) ? value.trim() : null
  if (name === 'allowfullscreen') return ''

  return value
}

const sanitizeAttributes = (tag: string, raw: string, wide = false) => {
  const allowed = (wide ? WIDE_ALLOWED_ATTRIBUTES : ALLOWED_ATTRIBUTES)[tag]
  if (!allowed && !wide) return ''

  const parts: string[] = []
  ATTRIBUTE.lastIndex = 0
  let match = ATTRIBUTE.exec(raw)

  while (match) {
    const name = match[1].toLowerCase()
    const rawValue = match[2] ?? match[3] ?? match[4] ?? ''
    const isAllowed = !!allowed?.has(name) || (wide && name === 'style')
    const value =
      wide && isAllowed ? wideAttributeValue(tag, name, rawValue) : rawValue

    if (
      isAllowed &&
      value !== null &&
      !(name === 'href' && !isSafeHref(value))
    ) {
      // `<` e `>` escapados: sem isso um `</h2>` dentro do valor corta o recorte
      // do título do SeoContent no meio do atributo. TNDC-427.
      const escaped = value
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      parts.push(`${name}="${escaped}"`)
    }

    match = ATTRIBUTE.exec(raw)
  }

  return parts.length ? ` ${parts.join(' ')}` : ''
}

export const sanitizeSeoHtml = (
  html?: string | null,
  { allowlist = 'default' }: SanitizeSeoHtmlOptions = {}
): string => {
  if (!html) return ''

  const wide = allowlist === 'wide'
  const faq = allowlist === 'faq'
  // Saída que vai para dentro de outro bloco (card do SEO, <details> do FAQ): toda tag
  // aberta é fechada, e fechamento sem abertura é descartado.
  const balanced = wide || faq
  const allowedTags = wide
    ? WIDE_ALLOWED_TAGS
    : faq
    ? FAQ_ALLOWED_TAGS
    : ALLOWED_TAGS
  const unsafeContent = wide
    ? WIDE_ELEMENTS_WITH_UNSAFE_CONTENT
    : faq
    ? FAQ_ELEMENTS_WITH_UNSAFE_CONTENT
    : ELEMENTS_WITH_UNSAFE_CONTENT

  let previous = ''
  let cleaned = html
  // Reaplica até estabilizar: remover `<script>` de `<scr<script>ipt>` revela um
  // segundo `<script>` que uma passada só deixaria intacto.
  while (cleaned !== previous) {
    previous = cleaned
    cleaned = cleaned.replace(COMMENTS, '').replace(unsafeContent, '')
  }

  // Do iframe fica só a tag de abertura (o loop abaixo emite o fechamento): o
  // conteúdo de fallback sairia solto, e fora do YouTube/Vimeo ele sai inteiro.
  if (wide)
    cleaned = cleaned.replace(IFRAME_BLOCK, (_, attributes: string) =>
      sanitizeAttributes('iframe', attributes, true).includes(' src="')
        ? `<iframe${attributes}>`
        : ''
    )

  const escapeText = (text: string) => text.replace(/</g, '&lt;')

  const parts: string[] = []
  // Tags abertas (só na allowlist ampla). Tabela, link ou iframe sem fechamento vão
  // direto para o HTML do SSR e engolem o resto da página, inclusive o __NEXT_DATA__.
  const open: string[] = []
  let cursor = 0
  TAG.lastIndex = 0
  let match = TAG.exec(cleaned)

  while (match) {
    const [raw, closing, name, attributes] = match
    parts.push(escapeText(cleaned.slice(cursor, match.index)))
    cursor = match.index + raw.length

    const tag = name.toLowerCase()
    if (allowedTags.has(tag)) {
      // Um único h1 na página, o do título (mesma regra do servidor).
      const output = balanced && tag === 'h1' ? 'h2' : tag
      const selfClosing = VOID_TAGS.has(tag)

      if (!balanced) {
        parts.push(
          closing
            ? `</${output}>`
            : `<${output}${sanitizeAttributes(tag, attributes)}${
                selfClosing ? ' /' : ''
              }>`
        )
      } else if (closing) {
        // Fecha até a tag correspondente; fechamento sem abertura é descartado.
        const index = open.lastIndexOf(output)
        if (index !== -1)
          parts.push(...closeTags(open.splice(index, open.length - index)))
      } else if (tag === 'iframe') {
        parts.push(
          `<iframe${sanitizeAttributes(tag, attributes, true)}></iframe>`
        )
      } else {
        parts.push(
          `<${output}${sanitizeAttributes(tag, attributes, wide)}${
            selfClosing ? ' /' : ''
          }>`
        )
        if (!selfClosing) open.push(output)
      }
    }

    match = TAG.exec(cleaned)
  }

  parts.push(escapeText(cleaned.slice(cursor)))
  parts.push(...closeTags(open))

  return parts.join('')
}
